import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { Text } from '../../../../components/Text/Text';
import {
  ANIM,
  COLORS,
  FIXED_HEADER,
  HEADER_DESKTOP_PX,
  LAYOUT_CHROME,
  LUMIO_DRAWER,
  LUMIO_DRAWER_RANGE,
  LUMIO_DESKTOP,
  MOBILE_MQ,
  NAV_DESKTOP,
  SPACE,
} from '../../../../theme/tokens';

/* The video ad name editor, rendered in the breadcrumb slot while the immersive video editor is open (in place
   of the home/label breadcrumb). An unobtrusive underline-on-focus input so the name reads as a title. */
export const NameInput = styled.input`
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.text};
  background: transparent;
  border: 0;
  border-bottom: 1px solid transparent;
  padding: 2px 4px;
  min-width: 180px;
  max-width: 42vw;
  font-family: inherit;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &::placeholder {
    color: ${COLORS.textFaint};
    font-weight: 500;
  }
  &:hover {
    border-bottom-color: ${COLORS.hairline};
  }
  &:focus {
    outline: none;
    border-bottom-color: ${COLORS.accent};
  }
`;

/* The name editor: the title input plus a small pencil affordance so the owner sees the title is editable. */
export const NameEditWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const NameEditIcon = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: 0;
  background: transparent;
  color: ${COLORS.textFaint};
  cursor: pointer;
  transition: color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${COLORS.accent};
  }
`;

/* Template `.pg-body-top`. */
export const Root = styled.header.attrs({ className: 'pg-body-top' })`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px;

  ${NAV_DESKTOP} {
    /* Make the desktop bar the ${'HEADER_DESKTOP_PX'}px that the content tuck + header scrim already assume
       (its content is only ~80px tall). Besides aligning that latent constant, it lets the immersive Advanced
       Video canvas reach the EXACT viewport floor: the tucked content card ends at 100dvh only when the bar is
       really 88 (otherwise the board is clipped short by 88 - barHeight). */
    min-height: ${HEADER_DESKTOP_PX}px;
  }

  ${FIXED_HEADER} {
    position: fixed;
    /* Sit below the support-impersonation strip when present (--ows-imp-offset is its
     * measured height, 0 otherwise). !important so it beats the remote-config common
     * CSS, which pins this fixed mobile header to top 0 with a site-scoped (and so
     * higher-specificity) .-s-siteId .pg-body-top selector. */
    top: var(--ows-imp-offset, 0px) !important;
    left: 0;
    right: 0;
    /* Clear the status bar / notch now that viewport-fit=cover extends the page under
     * it (the black bar fills that strip). When the impersonation strip is above us it
     * already owns the notch inset, so MainLayout zeroes --ows-imp-safe-top to avoid
     * a double inset; otherwise it falls back to the real safe-area value. */
    padding: calc(12px + var(--ows-imp-safe-top, env(safe-area-inset-top))) 12px 12px;
    background: ${LAYOUT_CHROME.topBarMobileBg};
    z-index: 30;
  }
`;

/* Template `.top-breadcrumb`. */
export const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  ${FIXED_HEADER} {
    display: none;
  }
`;

/* Immersive-mode back button, the leftmost item in the breadcrumb row (only rendered while immersive). Its
   own margin-right separates it from the home mark; align-items:center on Breadcrumb keeps it on the label's
   line. Same surface/border as the (slid-out) side menu so it reads as the chrome the back button restores. */
export const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin-right: 8px;
  padding: 0;
  border-radius: 12px;
  color: ${COLORS.text};
  background: ${LAYOUT_CHROME.sideMenuBg};
  border: 1px solid ${LAYOUT_CHROME.contentBorder};
  cursor: pointer;
  transition:
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${COLORS.surfaceInset};
    transform: translateY(-1px);
  }
`;

export const HomeLink = styled(Link)`
  display: flex;
`;

/* Template `.icn`. */
// The whitelabel mark is rendered as a MASK tinted with a themed colour (not a
// raw <img>), so it adapts to light/dark instead of being a fixed dark/white SVG.
export const HomeIcon = styled.span<{ $src: string }>`
  display: block;
  width: 24px;
  height: 24px;
  background-color: ${COLORS.heading};
  -webkit-mask: url(${(p) => p.$src}) center / contain no-repeat;
  mask: url(${(p) => p.$src}) center / contain no-repeat;
`;

/* Template `.ficn` — the flaticon separator dot. */
export const SepDot = styled.i`
  display: block;
  font-size: 10px;
  line-height: 10px;
  color: ${LAYOUT_CHROME.breadcrumbDot};
`;

/* Wraps the `.lbl` so the section label links to its area root (like the home icon). */
export const LabelLink = styled(Link)`
  display: flex;
  text-decoration: none;
`;

/* Template `.lbl`. */
export const Label = styled(Text).attrs({ tag: 'span' as const })`
  color: ${LAYOUT_CHROME.breadcrumbLabel};
  font-size: 20px;
  font-weight: 400;
  line-height: 100%;
`;

/* Template `.top-utils`. The row centres its controls on one line: the brand
   selector (the tall 48px element), the wallet link and the Lumio trigger all
   share a common vertical centre via `align-items: center`, and each control
   collapses its own line-box (no descender slack) so their glyphs line up. */
/* Subtle hairline between the wallet balance and the AI-credits chip. */
export const WalletCreditsSep = styled.span`
  width: 1px;
  height: 18px;
  flex: 0 0 auto;
  background: ${COLORS.hairline};
`;

export const Utils = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 16px;
  line-height: 0;
  /* Nudge the brand selector + wallet off the content card's scrollbar. */
  margin-right: 20px;

  ${FIXED_HEADER} {
    width: 100%;
    /* Brand selector stays left; the wallet + credits cluster is pushed to the RIGHT as one group
       (WalletLink gets margin-left:auto), with a tight 12px gutter around the hairline separator. */
    justify-content: flex-start;
    gap: 12px;
    margin-right: 0;
    /* Extra breathing room to the right of the wallet on mobile. */
    padding-right: 10px;
  }
`;

/* Template `.navbar-wallet-link-wrapper`. */
export const WalletLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;

  /* On the phone header, absorb the free space so the wallet + hairline + credits sit as one
     right-aligned cluster (the brand selector keeps the left). */
  ${FIXED_HEADER} {
    margin-left: auto;
  }
`;

/* Stands in for WalletLink while it is hidden (the Newton showcase): on the phone header it takes the
   free space, so the credits + Lumio cluster still sits on the right. Not drawn on desktop. */
export const ClusterPush = styled.span`
  display: none;

  ${FIXED_HEADER} {
    display: block;
    margin-left: auto;
  }
`;

export const WalletAmount = styled(Text).attrs({ tag: 'span' as const })`
  color: ${LAYOUT_CHROME.walletAmount};
  font-size: 16px;
  font-weight: 300;
  line-height: 20px;
`;

/* Lumio drawer trigger — the multicolour Lumio mark in a round, centred hit area,
   sitting just after the wallet. Shown ONLY on the mid-desktop range where the
   docked Lumio column is hidden; the 36px square keeps its optical centre on the
   row's centre line, beside the 48px brand logo and the wallet glyph. */
export const LumioTrigger = styled.button<{ $desktop?: boolean }>`
  display: none;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background: transparent;
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ease;

  &:hover,
  &:focus-visible {
    background: ${LUMIO_DRAWER.triggerHoverBg};
  }

  svg,
  img {
    display: block;
    width: 24px;
    height: 24px;
  }

  ${LUMIO_DRAWER_RANGE} {
    display: inline-flex;
  }

  /* Phones: the header owns Lumio (the bottom bar is navigation only); it opens the mobile overlay. */
  @media ${MOBILE_MQ} {
    display: inline-flex;
  }

  /* Wide desktop: only while the docked column is collapsed (it reopens it). */
  ${({ $desktop }) => ($desktop ? `${LUMIO_DESKTOP} { display: inline-flex; }` : '')}
`;

/* Local Studio only: opens the active brand's site at its internal host. A quiet outlined pill, so a dev affordance
   never competes with the brand selector or the wallet; icon-only on the phone header, where the row is tight. */
export const InternalSiteLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  flex: 0 0 auto;
  height: 28px;
  padding: 0 ${SPACE[8]};
  border: 1px solid ${COLORS.hairline};
  border-radius: 9999px;
  color: ${COLORS.textMuted};
  text-decoration: none;
  line-height: 1;
  transition:
    color ${ANIM.INTERACT_MS}ms ease,
    border-color ${ANIM.INTERACT_MS}ms ease;

  &:hover,
  &:focus-visible {
    color: ${COLORS.text};
    border-color: ${COLORS.textFaint};
  }
`;

export const InternalSiteLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;

  ${FIXED_HEADER} {
    display: none;
  }
`;
